import { Component, Injector, Input, OnInit, computed, inject, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseModalComponent } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { AccountType, FinancialAccount, PostingModule, SubledgerType } from '../../../../models/financial-account/financial-account';
import { LocaleHelperService } from '../../../../services/locale-helper.service';
import { postingModuleOptions as buildPostingModuleOptions } from '../../../../utilities/posting-module.util';
import { currencyLabelFromApi } from '../../../../utilities/currency.util';

interface Option<T> {
  label: string;
  value: T;
}

interface AccountEditForm {
  nameEn: FormControl<string>;
  nameEs: FormControl<string>;
  postingModules: FormControl<PostingModule[]>;
}

/**
 * Edits an existing financial (GL) account. Only the name and the authorized posting modules are editable —
 * everything else is a decision the account's history now depends on:
 *
 * <ul>
 *   <li><b>Number</b> encodes where the account sits in the hierarchy.</li>
 *   <li><b>Account type</b> drives NormalBalance server-side, and the tree rolls balances up blindly, so
 *       changing it would silently corrupt the parent's total.</li>
 *   <li><b>Currency</b> labels money already posted; changing it relabels history without reconverting it.</li>
 *   <li><b>Subledger type</b> is the key existing subledger rows were written under.</li>
 * </ul>
 *
 * <p>Those are shown read-only rather than hidden, so the reason a field cannot be changed is visible
 * instead of merely absent.</p>
 */
@Component({
  selector: 'app-account-edit-modal',
  templateUrl: './account-edit-modal.component.html',
  styleUrl: './account-edit-modal.component.scss',
})
export class AccountEditModalComponent extends BaseModalComponent<FinancialAccount | null> implements OnInit {
  /** The account being edited. */
  @Input({ required: true }) account!: FinancialAccount;
  /** All company accounts — used to name the parent. */
  @Input() accounts: FinancialAccount[] = [];

  private readonly translate = inject(TranslateService);
  private readonly locale = inject(LocaleHelperService);

  readonly form: FormGroup<AccountEditForm> = new FormGroup<AccountEditForm>({
    nameEn: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    nameEs: new FormControl<string>('', { nonNullable: true }),
    postingModules: new FormControl<PostingModule[]>([], { nonNullable: true }),
  });

  readonly isSubledger = signal(false);
  readonly postingModuleOptions: Option<PostingModule>[] = buildPostingModuleOptions((k, f) => this.t(k, f));

  readonly parentLabel = computed(() => {
    const parent = this.accounts.find((a) => a.id === this.account?.parentAccountId);
    if (!parent) return this.t('COA_EditAccount_NoParent', 'None (root account)');
    return `${parent.accountNumber} · ${this.locale.getLocalizedValue(parent.accountName) || parent.accountNumber}`;
  });

  /** Reads return the Currency enum as a number; show the code. */
  readonly currencyLabel = computed(() => currencyLabelFromApi(this.account?.currency) || '—');

  readonly typeLabel = computed(() => {
    switch (this.account?.accountType) {
      case AccountType.Asset:
        return this.t('COA_Type_Asset', 'Asset');
      case AccountType.Liability:
        return this.t('COA_Type_Liability', 'Liability');
      case AccountType.Equity:
        return this.t('COA_Type_Equity', 'Equity');
      case AccountType.Revenue:
        return this.t('COA_Type_Revenue', 'Revenue');
      case AccountType.Expense:
        return this.t('COA_Type_Expense', 'Expense');
      default:
        return '';
    }
  });

  readonly subledgerLabel = computed(() => {
    switch (this.account?.subledgerType) {
      case SubledgerType.Provider:
        return this.t('COA_Subledger_Provider', 'Provider');
      case SubledgerType.Client:
        return this.t('COA_Subledger_Client', 'Client');
      case SubledgerType.Bank:
        return this.t('COA_Subledger_Bank', 'Bank');
      case SubledgerType.InternalCompany:
        return this.t('COA_Subledger_InternalCompany', 'Internal company');
      case SubledgerType.Employee:
        return this.t('COA_Subledger_Employee', 'Employee');
      case SubledgerType.PettyCashFund:
        return this.t('COA_Subledger_PettyCashFund', 'Petty cash fund');
      default:
        return this.t('COA_Subledger_None', 'None (ordinary account)');
    }
  });

  constructor(injector: Injector) {
    super(injector);
  }

  ngOnInit(): void {
    this.isSubledger.set(this.account?.subledgerType != null);
    this.form.setValue({
      nameEn: this.account?.accountName?.en ?? '',
      nameEs: this.account?.accountName?.es ?? '',
      postingModules: [...(this.account?.authorizedPostingModules ?? [])],
    });
    // Mirrors the server invariant: a subledger account must keep at least one authorized module.
    if (this.isSubledger()) this.form.controls.postingModules.addValidators(Validators.required);
  }

  private t(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.getRawValue();
    // PUT replaces the whole document, so everything untouched is carried over verbatim — sending only the
    // edited fields would blank the rest.
    const updated: FinancialAccount = {
      ...this.account,
      accountName: { en: v.nameEn.trim(), es: (v.nameEs || v.nameEn).trim() },
      authorizedPostingModules: v.postingModules ?? [],
      // Read as the enum number, written as the code — the entity's Currency is a string, so echoing
      // back what we received would fail to bind.
      currency: currencyLabelFromApi(this.account?.currency),
    };
    this.close({ action: 'save', data: updated });
  }

  onClose(): void {
    this.close({ action: 'close', data: null });
  }
}
